// 求人票(年度別編集画面)の「確定」時にsearchIndexへ反映するための処理。
//
// 3.recruit/navigation/publishLogic.ts の searchIndexRegister と同じ
// searchIndex/{companyId}_2027 ドキュメントに書き込む(IDのスキームは変更しない。
// searchIndexのIDを一気に変えるのは、参照している全箇所を同時に切り替える
// 必要があり、既存の求人編集(3.recruit)側の公開フローと衝突するリスクが高いため
// 今回は行わない)。
//
// summary/point/strong/person などの「求人編集(3.recruit)」側が持つデータは
// 引き続きフラットドキュメント recruits/{companyId}_2027 本体から読む。
// employment20/info/applicationInfo/hashTag/target(求人票専有・年度別データ)は、
// recruits/{companyId}_2027/jobPostings/ 配下(親は同じフラットドキュメント)から
// 「データが入っている最新の年度」を優先して使う(例: 2027と2028が両方あれば
// 2028を優先)。まだ年度別サブコレクションにデータが無い会社(このJobPostings
// 編集画面を一度も使っていない会社)は、従来通りフラットドキュメント本体の
// employment20等にフォールバックする。
//
// publishLogic.ts を直接importして再利用できる部分(キーワード抽出・タグの
// 再分類・店舗名の整形・配列の重複除去・タグ辞書)はそのままimportし、
// publishLogic.ts自体は一切変更しない(3.recruit側の挙動に影響を与えないため)。
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import {
  computeHasFlags,
  cutAfterTenRough,
  dedupeObjectArrays,
  extractKeywords,
  findLatestJobPosting,
  reclassifyTags,
  loadSendingWhitelist,
  addressPrefOf,
  toSearchIndexPerson,
  textQuery,
} from '../../../3.recruit/navigation/publishLogic';

function pushSafe(arr: string[], value?: string | null) {
  if (value && value.trim() !== '') {
    arr.push(value);
  }
}

export async function registerJobPostingSearchIndex(companyId: string) {
  const db = getFirestore();

  const legacyRef = doc(db, 'recruits', `${companyId}_2027`);
  const legacySnap = await getDoc(legacyRef);

  if (!legacySnap.exists()) {
    console.error('対象ドキュメントが存在しません(searchIndex更新スキップ)');
    return;
  }

  const legacyData = legacySnap.data() || {};
  const latestJobPosting = await findLatestJobPosting(companyId);

  // 年度別・求人票専有データは、サブコレクション側にデータがあればそちらを優先。
  // 無ければ(＝この会社がまだ新しい求人票編集画面を使っていない)旧ドキューメント
  // 自身のフィールドにフォールバックする。
  const employment20 = latestJobPosting?.employment20 ?? legacyData.employment20 ?? {};
  const applicationInfo = latestJobPosting?.applicationInfo ?? legacyData.applicationInfo ?? {};
  const hashTag = latestJobPosting?.hashTag ?? legacyData.hashTag ?? {};
  const target = latestJobPosting?.target ?? legacyData.target ?? {};
  const info = latestJobPosting?.info ?? legacyData.info ?? {};

  let searchSummary: string[] = [];

  // --------------------
  // 給与 salaryTags
  // --------------------
  const salaryTags: string[] = [];

  const total =
    employment20.salaryType === '年俸制'
      ? [
          Number(employment20.baseSalary),
          Number(employment20.regularAllowance?.price1) * 12,
          Number(employment20.regularAllowance?.price2) * 12,
          Number(employment20.fixedOvertime?.price) * 12,
        ]
          .filter((v) => !isNaN(v))
          .reduce((sum, v) => sum + v, 0)
      : [
          Number(employment20.baseSalary),
          Number(employment20.regularAllowance?.price1),
          Number(employment20.regularAllowance?.price2),
          Number(employment20.fixedOvertime?.price),
        ]
          .filter((v) => !isNaN(v))
          .reduce((sum, v) => sum + v, 0);

  const salaryMan = Math.floor(total / 10000);
  const start = 17;
  const end = Math.max(salaryMan, 17);

  for (let i = start; i <= end; i++) {
    salaryTags.push(i.toString());
  }

  let storeCount = 0;
  let storeImage = '';
  let storeTags: string[] = [];
  let storePentagonHash: string[] = [];
  let storeType: string[] = [];

  let firstStore: any = null;
  const storesQ = query(collection(db, 'stores'), where('companyId', '==', companyId));
  const storesSnap = await getDocs(storesQ);

  storesSnap.forEach((storeDoc) => {
    const storeData = storeDoc.data();
    if (!storeData) return;

    storeCount = storeCount + 1;

    if (storeData.number === 1) {
      storeImage = storeDoc.id;
      firstStore = storeData;
    }

    const storeName = cutAfterTenRough(storeData.name);
    searchSummary.push(storeName);

    storeTags.push(...(storeData.stationTags ?? []));
    storePentagonHash.push(...(storeData.pentagonHash ?? []));
    storeType.push(storeData.type);
  });

  // --------------------
  // summary / point / strong / info (summaryは求人編集(3.recruit)と共有のため旧ドキュメントから)
  // --------------------
  const s = legacyData.summary || {};
  const p = legacyData.point || {};
  const st = legacyData.strong || {};

  const texts: string[] = [];

  pushSafe(texts, s.title);
  pushSafe(texts, s.companyPr);
  pushSafe(texts, p.title1);
  pushSafe(texts, p.title2);
  pushSafe(texts, p.title3);
  pushSafe(texts, st.item1?.title);
  pushSafe(texts, st.item1?.desc);
  pushSafe(texts, st.item2?.title);
  pushSafe(texts, st.item2?.desc);
  pushSafe(texts, st.item3?.title);
  pushSafe(texts, st.item3?.desc);

  const searchText = texts.join(' ');
  const searchKeywords = extractKeywords(searchText, textQuery);

  searchSummary = [s.name, info.companyName, info.companyNameKana, ...searchSummary];

  // ---- タグ再分類 ----
  const tags1 = Array.isArray(hashTag?.tags1) ? hashTag.tags1 : [];
  const tags2 = Array.isArray(hashTag?.tags2) ? hashTag.tags2 : [];
  const tags3 = Array.isArray(hashTag?.tags3) ? hashTag.tags3 : [];

  const { hashTagsWorkStyle, hashTagsBenefit, hashTagsEducation } = reclassifyTags(tags1, tags2, tags3);

  const businessMap: Record<string, number | undefined> = {
    理容: applicationInfo?.recruitsBarber,
    美容: applicationInfo?.recruitsBeauty,
    メイク: applicationInfo?.recruitsMake,
    アイ: applicationInfo?.recruitsEye,
    ネイル: applicationInfo?.recruitsNeil,
    ブライダル: applicationInfo?.recruitsBridal,
    エステ: applicationInfo?.recruitsAesthetic,
  };

  const businessTags: string[] = [
    ...Object.entries(businessMap)
      .filter(([_, count]) => (count ?? 0) > 0)
      .map(([name]) => name),
  ];

  if (info?.business?.contents?.includes('その他')) {
    businessTags.push('その他');
  }

  if (info?.business?.canConcurrent === true) {
    businessTags.push('兼任可能');
  }

  const sendingWhitelist = await loadSendingWhitelist();
  const hasFlags = await computeHasFlags(db, companyId);

  // --------------------
  // searchIndex 更新オブジェクト(IDは既存と同じ {companyId}_2027 のまま)
  // --------------------
  const searchIndex = {
    id: `${companyId}_2027`,
    name: legacyData.summary?.name,
    title: legacyData.summary?.title,
    companyId: legacyData.companyId,

    image: legacyData.summary?.images?.[0] ?? '',
    storeImage: storeImage,

    ...hasFlags,

    jobPostingsStatus: (latestJobPosting?.jobPostingsStatus ?? legacyData.jobPostingsStatus) === 1,
    // 「求人ページが公開中か」。トップページ・アプリはこれが true のとき求人ページの
    // 写真(image)、false のとき求人票の店舗写真(storeImage)を出すため、固定で true に
    // すると求人ページ未公開の会社が noimage になる。旧フロー(1.manual)と同じ判定。
    recruitStatus: legacyData.recruitStatus === 1,

    randomValue: Math.random(),

    person: toSearchIndexPerson(legacyData.person),

    searchSummary: searchSummary,
    searchKeywords: searchKeywords,

    salaryTags: salaryTags,
    business: businessTags,

    storeTags: storeTags,
    pentagonHash: storePentagonHash,
    storeType: storeType,
    addressPref: addressPrefOf(info, firstStore),

    hashTagsEducation,
    hashTagsBenefit,
    hashTagsWorkStyle,

    // 募集区分タブ(4.target.tsx)は「専門(20歳卒)」を外せないUIなので、タブ自体が
    // 未保存(target.targetが空)の会社も専門(20歳卒)は対象として扱う(2026-11-04)。
    target: target?.target?.length > 0 ? target.target : ['専門(20歳卒)'],

    storeCount: storeCount,

    sending_2027: (legacyData['2027_sending'] ?? [])
      .filter((id: string) => sendingWhitelist.has(id))
      .filter((id: string, i: number, arr: string[]) => arr.indexOf(id) === i),
    sending_2028: (legacyData['2028_sending'] ?? [])
      .filter((id: string) => sendingWhitelist.has(id))
      .filter((id: string, i: number, arr: string[]) => arr.indexOf(id) === i),
  };

  dedupeObjectArrays(searchIndex);

  await setDoc(doc(db, 'searchIndex', `${companyId}_2027`), searchIndex, {
    merge: true,
  });
}
